import React, { forwardRef, useContext, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import Login from 'src/components/Login/Login';
import Popup from 'src/components/Popup/Popup';
import { Section } from 'src/components/Section';
import PersonalDetails, { IPersonalDetails } from 'src/components/UserDetails/PersonalDetails/PersonalDetails';
import Checkbox from 'src/components/form-components/Checkbox/Checkbox';
import SecondaryButton from 'src/components/form-components/SecondaryButton/SecondaryButton';
import TextInput, { TextInputType, createInputId } from 'src/components/form-components/TextInput/TextInput';
import ValidationMessage from 'src/components/form-components/ValidationMessage/ValidationMessage';
import { AppContext } from 'src/contexts/AppContext';
import { BookingContext } from 'src/contexts/BookingFlowContext';
import { EmbedContext } from 'src/contexts/EmbedContext';
import { ReservationEndContext } from 'src/contexts/ReservationEndContext';
import { defaultRequiredInfo } from 'src/entities/company';
import { IReservationRequiredFieldProps, isEmailRequired, isNameRequired, isPhoneNumberRequired } from 'src/entities/reservation';
import { IReservationEndUser } from 'src/entities/user';
import FormValidator, { IValidation } from 'src/services/formValidator';
import globalMessages from 'src/services/globalMessages';
import { signupFields, signupRules } from 'src/services/validations/signupRules';
import { createValidation } from '../../functions/createFunctions';

const messages = defineMessages({
    reservationEndLogin: {
        id: 'reservationEndLogin',
        defaultMessage: 'Van fiókom, bejelentkezem'
    },
    or: {
        id: 'or',
        defaultMessage: 'Vagy'
    },
    signUpOnReservation: {
        id: 'signUpOnReservation',
        defaultMessage: 'Fiók létrehozása'
    }
});

export interface IPersonalDetailsSectionProps {
    validation: IValidation;
    submitted: boolean;
}

const PersonalDetailsSection = forwardRef<IPersonalDetails, IPersonalDetailsSectionProps> (({ validation, submitted }, ref) => {

    const { isAuthenticated, company, user, actions } = useContext(AppContext);
    const { hideUser } = useContext(EmbedContext)
    const { reservationEndUser, actions: { handleUserChange } } = useContext(BookingContext);
    const { shouldSignUp, actions: { setShouldSignUp } } = useContext(ReservationEndContext)

    const [isLoginVisible, setLoginVisible] = useState(false);

    const requiredFieldInfo: IReservationRequiredFieldProps = {
        isAuthenticated,
        requiredFieldInfo: company ? company.RequiredFieldInfo : defaultRequiredInfo
    }

    const toggleLogin = () => {
        setLoginVisible(!isLoginVisible);
    }

    const validator = new FormValidator(signupRules);
    const passwordValidation = createValidation(validator, reservationEndUser, submitted);

    return (
        <Section title={globalMessages.personalDetails}>
            <div className='reservation-end__user-details'>
                <form
                    id={'reservation-end-form'}
                    autoComplete='off'
                    className='reservation-end__form'
                    noValidate={true}
                >
                    {!user && !hideUser &&
                        <Checkbox
                            checked={shouldSignUp ?? false}
                            onChange={setShouldSignUp}
                            className='reservation-end__sign-up-checkbox'
                        >
                            <FormattedMessage {...messages.signUpOnReservation} />
                        </Checkbox>
                    }
                    <PersonalDetails
                        ref={ref}
                        defaultUser={reservationEndUser}
                        validation={validation}
                        handleUserChange={handleUserChange}
                        isEmailRequired={shouldSignUp || isEmailRequired(requiredFieldInfo)}
                        isPhoneNumberRequired={shouldSignUp || isPhoneNumberRequired(requiredFieldInfo)}
                        isNameRequired={shouldSignUp || isNameRequired(requiredFieldInfo)}
                        adminCountryCode={company?.DefaultCountryCode}
                    />
                    {!user && !hideUser && shouldSignUp && reservationEndUser &&
                        <>
                            <TextInput
                                type={TextInputType.Password}
                                className='reservation-end__password'
                                fieldName={signupFields.Password}
                                label={globalMessages.password}
                                onChange={handleUserChange}
                                required={true}
                                value={reservationEndUser[signupFields.Password as keyof IReservationEndUser] as string || ''}
                                id={createInputId(signupFields.Password)}
                            />
                            <ValidationMessage
                                className='reservation-end__password-validation'
                                message={passwordValidation.fields[signupFields.Password].message}
                                formatValues={passwordValidation.fields[signupFields.Password]?.messageValues}
                            />
                        </>
                    }
                </form>

                {!user && !hideUser && <>
                    <div className='reservation-end-separator' >
                        <div className='reservation-end-separator__line' />
                        <span className='reservation-end-separator__text'>
                            <FormattedMessage {...messages.or} />
                        </span>
                        <div className='reservation-end-separator__line' />
                    </div>
                    <SecondaryButton autoFocus={true} className='reservation-end-login' caption={messages.reservationEndLogin} onClick={toggleLogin} />
                    <Popup closePopup={toggleLogin} visible={isLoginVisible}>
                        <Login setUser={actions.setUser} />
                    </Popup>
                </>}
            </div>
        </Section>
    )
});

export default PersonalDetailsSection;
